import {
  isDebuffKind,
  isSpawnLive,
  isTimedEffectKind,
  POWER_UP_KINDS,
  type ActiveEffect,
  type PowerUpKind,
  type PowerUpSpawn,
  type TimedEffectKind,
} from '../shared/power-ups';
import { seededRandom } from './seeded-random';

/**
 * Chaos rules: when power-ups appear, what consuming one does, and how effects shape a
 * participant's later words. Everything here is pure and server-only; `room.ts` orders
 * persistence and broadcasts, the client only reads the resulting spawns and effects.
 */

/** Play settles before the first spawn appears. */
export const SPAWN_LEAD_MS = 5_000;
export const SPAWN_INTERVAL_MS = 8_000;
/** A spawn nobody reaches disappears; a spawn with less than half this left is never scheduled. */
export const SPAWN_LIFETIME_MS = 10_000;
export const EFFECT_DURATION_MS: Record<TimedEffectKind, number> = {
  double: 10_000,
  freeze: 3_000,
  tax: 8_000,
  spin: 8_000,
  fog: 6_000,
};
export const GOLD_BONUS_POINTS = 300;
export const DOUBLE_MULTIPLIER = 2;
export const TAX_MULTIPLIER = 0.5;
/** Reveal prefers a word worth finding over a three-letter filler. */
const REVEAL_MIN_WORD_LENGTH = 5;

/** The participant fields chaos reads and writes; `StoredRoundParticipant` satisfies it. */
export interface EffectHolder {
  id: string;
  finished?: boolean;
  effects?: ActiveEffect[];
  shield?: boolean;
}

/** Read-only view of a participant's effects; accepted by every query below. */
export type EffectReader = Readonly<{ effects?: readonly Readonly<ActiveEffect>[] }>;

export interface PickupOutcome {
  kind: PowerUpKind;
  by: string;
  /** Participants whose state changed: the collector for boosts, everyone else for debuffs. */
  targets: string[];
  /** Participants whose shield absorbed this debuff instead. */
  shielded: string[];
  /** Deadline of the timed effect applied to `targets`; null for instant kinds. */
  until: number | null;
  /** Points added to the consuming word. */
  bonus: number;
}

/**
 * Deterministic spawn schedule for a live round: evenly spaced, never two live spawns on one
 * tile, kinds drawn from a seeded generator so a round's schedule can be regenerated in tests.
 */
export function scheduleSpawns(
  seed: string,
  tileCount: number,
  startAt: number,
  endAt: number,
): PowerUpSpawn[] {
  const random = seededRandom(seed);
  const spawns: PowerUpSpawn[] = [];
  for (let at = startAt + SPAWN_LEAD_MS; at + SPAWN_LIFETIME_MS / 2 <= endAt; at += SPAWN_INTERVAL_MS) {
    const occupied = new Set(spawns.filter((spawn) => spawn.until > at).map((spawn) => spawn.tile));
    const free = Array.from({ length: tileCount }, (_, tile) => tile).filter((tile) => !occupied.has(tile));
    if (free.length === 0) continue;
    spawns.push({
      id: `p${spawns.length + 1}`,
      kind: POWER_UP_KINDS[Math.floor(random() * POWER_UP_KINDS.length)]!,
      tile: free[Math.floor(random() * free.length)]!,
      at,
      until: Math.min(at + SPAWN_LIFETIME_MS, endAt),
    });
  }
  return spawns;
}

/** First claimed spawn in path order that is still live; never substitute an unobserved spawn. */
export function spawnOnPath(
  spawns: readonly Readonly<PowerUpSpawn>[],
  path: readonly number[],
  pickupIds: readonly string[],
  now: number,
): PowerUpSpawn | null {
  for (const tile of path) {
    const spawn = spawns.find(
      (candidate) =>
        candidate.tile === tile && pickupIds.includes(candidate.id) && isSpawnLive(candidate, now),
    );
    if (spawn) return spawn;
  }
  return null;
}

export function activeEffects(holder: EffectReader, now: number): ActiveEffect[] {
  return (holder.effects ?? []).filter((effect) => effect.until > now).map((effect) => ({ ...effect }));
}

export function hasEffect(holder: EffectReader, kind: TimedEffectKind, now: number): boolean {
  return activeEffects(holder, now).some((effect) => effect.kind === kind);
}

/** Multiplier for a word accepted now; stored into its points so scoring never re-derives it. */
export function scoreMultiplier(holder: EffectReader, now: number): number {
  let multiplier = 1;
  if (hasEffect(holder, 'double', now)) multiplier *= DOUBLE_MULTIPLIER;
  if (hasEffect(holder, 'tax', now)) multiplier *= TAX_MULTIPLIER;
  return multiplier;
}

/**
 * Consume `spawn` for `collectorId`, mutating the participants it affects. Timed effects of
 * the same kind extend rather than stack. A shield absorbs one debuff and is spent by it.
 */
export function applyPickup(
  spawn: PowerUpSpawn,
  participants: readonly EffectHolder[],
  collectorId: string,
  now: number,
): PickupOutcome {
  spawn.takenBy = collectorId;
  const outcome: PickupOutcome = {
    kind: spawn.kind,
    by: collectorId,
    targets: [],
    shielded: [],
    until: null,
    bonus: 0,
  };
  const collector = participants.find((participant) => participant.id === collectorId);
  if (spawn.kind === 'gold') {
    outcome.bonus = GOLD_BONUS_POINTS;
    if (collector) outcome.targets.push(collector.id);
    return outcome;
  }
  if (spawn.kind === 'reveal') {
    if (collector) outcome.targets.push(collector.id);
    return outcome;
  }
  if (spawn.kind === 'shield') {
    if (collector) {
      collector.shield = true;
      outcome.targets.push(collector.id);
    }
    return outcome;
  }
  if (!isTimedEffectKind(spawn.kind)) return outcome;
  const kind = spawn.kind;
  const until = now + EFFECT_DURATION_MS[kind];
  outcome.until = until;
  const recipients = isDebuffKind(kind)
    ? participants.filter((participant) => participant.id !== collectorId && !participant.finished)
    : collector
      ? [collector]
      : [];
  for (const recipient of recipients) {
    if (isDebuffKind(kind) && recipient.shield) {
      recipient.shield = false;
      outcome.shielded.push(recipient.id);
      continue;
    }
    addEffect(recipient, kind, until, now);
    outcome.targets.push(recipient.id);
  }
  return outcome;
}

/** The word a Reveal hands the collector: an unfound solution, preferably a longer one. */
export function revealWord(
  solutions: readonly string[],
  found: Readonly<Record<string, unknown>>,
): string | null {
  const unfound = solutions.filter((word) => !Object.hasOwn(found, word));
  return unfound.find((word) => word.length >= REVEAL_MIN_WORD_LENGTH) ?? unfound[0] ?? null;
}

function addEffect(holder: EffectHolder, kind: TimedEffectKind, until: number, now: number): void {
  const kept = activeEffects(holder, now).filter((effect) => effect.kind !== kind);
  const existing = holder.effects?.find((effect) => effect.kind === kind);
  kept.push({ kind, until: Math.max(until, existing?.until ?? 0) });
  holder.effects = kept;
}
